import React, { useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useReducedMotion,
  animate,
  useMotionValue,
} from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, X, ChevronDown } from "lucide-react";
import { HomeNav, Pill, ease } from "@/components/home/HomeNav";
import { HomeFooter } from "@/components/home/ContactFooter";
import { getCaseStudy } from "@/data/caseStudies";
import { useSeo } from "@/hooks/useSeo";

const viewport = { once: true, margin: "-70px" };
const rise = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease } },
};

const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={viewport}
    variants={{ hidden: rise.hidden, show: { ...rise.show, transition: { ...rise.show.transition, delay } } }}
    className={className}
  >
    {children}
  </motion.div>
);

const Stagger = ({ children, className = "", stagger = 0.1, delay = 0, as = "div", ...props }) => {
  const Tag = motion[as];
  return (
    <Tag
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      variants={{ hidden: {}, show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
};
const Item = ({ children, className = "", as = "div", ...props }) => {
  const Tag = motion[as];
  return <Tag variants={rise} className={className} {...props}>{children}</Tag>;
};

const Eyebrow = ({ children }) => (
  <p className="font-title text-[11px] sm:text-xs uppercase tracking-[0.22em] text-neutral-500">
    <span className="inline-block h-px w-8 align-middle mr-3 bg-white/25" />
    {children}
  </p>
);

const Divider = () => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={viewport}
    transition={{ duration: 1.4, ease }}
    className="h-px w-full bg-white/[0.08] origin-left"
  />
);

// Scroll-driven parallax wrapper for images
const Parallax = ({ children, className = "", amount = 60 }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [amount, -amount]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={{ y }} className="will-change-transform">{children}</motion.div>
    </div>
  );
};

// Animated number counter
const Counter = ({ value, prefix = "", suffix = "", display }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -80px 0px" });
  const reduce = useReducedMotion();
  const mv = useMotionValue(0);
  const [text, setText] = React.useState(reduce ? display : `${prefix}0${suffix}`);

  useEffect(() => {
    if (!inView) return;
    if (reduce) { setText(display); return; }
    const controls = animate(mv, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setText(`${prefix}${Math.round(v).toLocaleString("en-GB")}${suffix}`),
    });
    return () => controls.stop();
  }, [inView, reduce, value, prefix, suffix, display, mv]);

  return <span ref={ref}>{text}</span>;
};

const SectionWrap = ({ children, className = "" }) => (
  <section className={className}>
    <div className="mx-auto w-full max-w-[1300px] px-[6vw] lg:px-[5vw]">{children}</div>
  </section>
);

// Interactive before/after comparison slider (mouse + touch + keyboard)
const BeforeAfterSlider = ({ before, after, height = "h-[440px] sm:h-[620px]" }) => {
  const [pos, setPos] = React.useState(50);
  const [width, setWidth] = React.useState(0);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pct = ((clientX - r.left) / r.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  };

  useEffect(() => {
    const measure = () => ref.current && setWidth(ref.current.getBoundingClientRect().width);
    measure();
    window.addEventListener("resize", measure);
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid="cs-ba-slider"
      className={`relative w-full ${height} rounded-2xl overflow-hidden border border-white/10 bg-black select-none cursor-ew-resize shadow-[0_50px_140px_-50px_rgba(0,0,0,0.9)]`}
      onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}
    >
      {/* AFTER (base) */}
      <img src={after} alt="Redesigned website" draggable={false} className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none" />
      <span className="absolute top-4 right-4 z-10 rounded-full bg-white text-black px-3 py-1 font-title text-[10px] uppercase tracking-[0.18em]">After</span>
      {/* BEFORE (clipped) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt="Original website" draggable={false} style={{ width: width || "100%" }} className="absolute inset-0 h-full max-w-none object-cover object-top" />
        <span className="absolute top-4 left-4 rounded-full bg-black/70 backdrop-blur text-white px-3 py-1 font-title text-[10px] uppercase tracking-[0.18em] border border-white/15">Before</span>
      </div>
      {/* Handle */}
      <div className="absolute inset-y-0 z-20 -ml-px" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 w-0.5 bg-white/90" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white text-black grid place-items-center shadow-lg">
          <span className="flex items-center gap-0.5"><ArrowRight size={13} className="-scale-x-100" /><ArrowRight size={13} /></span>
        </div>
      </div>
      <input
        type="range" min="0" max="100" value={pos} aria-label="Reveal before and after"
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-x-0 bottom-0 z-30 w-full opacity-0 h-11 cursor-ew-resize"
      />
    </div>
  );
};


const CaseStudyPage = () => {
  const { slug } = useParams();
  const cs = getCaseStudy(slug);
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress: heroProg } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroProg, [0, 1], reduce ? [0, 0] : [0, 120]);
  const heroScale = useTransform(heroProg, [0, 1], reduce ? [1, 1] : [1, 1.08]);

  useSeo({
    title: cs ? cs.seoTitle : "Case Study | Jay Alminshawi",
    description: cs ? cs.seoDescription : "",
    path: cs ? `/work/${cs.slug}` : "/work",
    image: cs ? cs.heroMockup : "/android-chrome-512x512.png",
    type: "article",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!cs) return <Navigate to="/#work" replace />;

  return (
    <main data-testid="case-study-page" className="relative bg-[#09090b] text-white font-jakarta antialiased overflow-x-hidden">
      <HomeNav />

      {/* SECTION 01 — HERO */}
      <section ref={heroRef} data-testid="cs-hero" className="relative pt-28 sm:pt-36 lg:pt-44 pb-16 sm:pb-24 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[560px] w-[1200px] rounded-full bg-white/[0.045] blur-[170px] pointer-events-none" />
        <div className="mx-auto w-full max-w-[1300px] px-[6vw] lg:px-[5vw]">
          <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } } }}>
            <Item><Eyebrow>{cs.hero.eyebrow}</Eyebrow></Item>
            <Item as="h1" data-testid="cs-hero-headline"
              className="mt-7 font-title font-medium text-white tracking-tight leading-[1.04] text-balance text-4xl sm:text-6xl lg:text-[82px] max-w-[16ch]">
              {cs.hero.headline}
            </Item>
            <Item as="p" className="mt-8 max-w-2xl text-[15px] sm:text-lg text-neutral-400 leading-relaxed">
              {cs.hero.description}
            </Item>
          </motion.div>

          {/* Project details */}
          <Stagger as="dl" data-testid="cs-hero-details" delay={0.35} stagger={0.08}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-6 border-t border-white/[0.08] pt-10">
            {[
              ["Client", cs.client],
              ["Industry", cs.industry],
              ["Project date", cs.date],
              ["Website", cs.website],
            ].map(([k, v]) => (
              <Item key={k}>
                <dt className="font-title text-[10.5px] uppercase tracking-[0.2em] text-neutral-600 mb-2">{k}</dt>
                <dd className="text-[13.5px] sm:text-[14.5px] text-neutral-200 leading-snug">{v}</dd>
              </Item>
            ))}
            <Item className="col-span-2 sm:col-span-4">
              <dt className="font-title text-[10.5px] uppercase tracking-[0.2em] text-neutral-600 mb-3">Services</dt>
              <dd className="flex flex-wrap gap-2">
                {cs.services.map((s) => (
                  <span key={s} className="rounded-full bg-white/[0.06] border border-white/[0.08] px-3 py-1.5 text-[11px] text-neutral-300">{s}</span>
                ))}
              </dd>
            </Item>
          </Stagger>

          {/* Hero visual */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease, delay: 0.5 }}
            className="mt-16 sm:mt-20"
          >
            <div className="relative rounded-[18px] sm:rounded-[24px] overflow-hidden border border-white/10 bg-black shadow-[0_60px_160px_-50px_rgba(0,0,0,0.9)]">
              <motion.img
                style={{ y: heroY, scale: heroScale }}
                src={cs.heroMockup}
                alt={`${cs.client} website shown on a laptop`}
                className="w-full h-auto block will-change-transform"
              />
            </div>
          </motion.div>
        </div>

        {!reduce && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }}
            className="mt-14 flex flex-col items-center gap-2 text-neutral-600">
            <span className="font-title text-[10px] uppercase tracking-[0.3em]">Scroll</span>
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
              <ChevronDown size={16} />
            </motion.span>
          </motion.div>
        )}
      </section>

      {/* SECTION 02 — THE PROBLEM */}
      <SectionWrap className="py-20 sm:py-28 lg:py-36">
        <Divider />
        <div className="mt-16 sm:mt-24 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>{cs.problem.number}</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 data-testid="cs-problem-headline" className="mt-6 font-title font-medium text-white tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-[52px] text-balance">
                {cs.problem.headline}
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-7 text-[15px] sm:text-base text-neutral-400 leading-relaxed max-w-md">{cs.problem.intro}</p>
            </Reveal>
          </div>
          <Stagger className="lg:col-span-7 lg:pt-2" stagger={0.1}>
            {cs.problem.points.map((p, i) => (
              <Item as="div" key={p.title} data-testid={`cs-problem-${i + 1}`}
                className="grid grid-cols-[auto_1fr] gap-5 sm:gap-8 py-7 border-t border-white/[0.08] last:border-b">
                <span className="font-title text-sm text-neutral-600 tabular-nums pt-1">0{i + 1}</span>
                <div>
                  <h3 className="font-title text-lg sm:text-xl font-medium tracking-tight text-white">{p.title}</h3>
                  <p className="mt-2.5 text-[14px] sm:text-[15px] text-neutral-500 leading-relaxed">{p.body}</p>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>

        {/* Original website — genuine before screenshot */}
        <Reveal delay={0.1} className="mt-16 sm:mt-20">
          <p className="font-title text-xs uppercase tracking-[0.2em] text-neutral-500 mb-6">The original website</p>
          <div data-testid="cs-before-image" className="max-w-3xl rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d10] shadow-[0_50px_140px_-50px_rgba(0,0,0,0.9)]">
            <div className="flex items-center gap-1.5 px-4 h-9 border-b border-white/[0.06] bg-white/[0.02]">
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="ml-3 text-[11px] text-neutral-500 font-mono-grotesk">Before the rebuild</span>
            </div>
            <Parallax amount={40} className="max-h-[520px]">
              <img src={cs.beforeImage} alt="EDN Renovation Group's original AI-generated website" className="w-full h-auto block" loading="lazy" />
            </Parallax>
          </div>
        </Reveal>
      </SectionWrap>

      {/* SECTION 03 — THE TRANSFORMATION */}
      <SectionWrap className="py-20 sm:py-28 lg:py-36">
        <Divider />
        <div className="mt-16 sm:mt-24 max-w-3xl">
          <Reveal><Eyebrow>{cs.transformation.number}</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 data-testid="cs-transformation-headline" className="mt-6 font-title font-medium text-white tracking-tight leading-[1.06] text-3xl sm:text-5xl lg:text-[60px] text-balance">
              {cs.transformation.headline}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 text-[15px] sm:text-lg text-neutral-400 leading-relaxed">{cs.transformation.intro}</p>
          </Reveal>
        </div>

        {/* Real website reveal */}
        <Reveal delay={0.1} className="mt-16 sm:mt-20">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d10] shadow-[0_50px_140px_-50px_rgba(0,0,0,0.9)]">
            <div className="flex items-center gap-1.5 px-4 h-9 border-b border-white/[0.06] bg-white/[0.02]">
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="ml-3 text-[11px] text-neutral-500 font-mono-grotesk">{cs.website}</span>
            </div>
            <Parallax amount={40} className="max-h-[520px]">
              <img src={cs.showcaseFull} alt={`${cs.client} website`} className="w-full h-auto block" loading="lazy" />
            </Parallax>
          </div>
        </Reveal>

        {/* Features */}
        <div className="mt-16 sm:mt-24 grid md:grid-cols-2 gap-x-10 gap-y-14">
          {cs.transformation.features.map((f, i) => (
            <Reveal key={f.title} delay={(i % 2) * 0.1}>
              <div data-testid={`cs-feature-${i + 1}`} className="h-full">
                <p className="font-title text-[10.5px] uppercase tracking-[0.2em] text-neutral-600">{f.k}</p>
                <h3 className="mt-3 font-title text-xl sm:text-2xl font-medium tracking-tight text-white">{f.title}</h3>
                <p className="mt-4 text-[14.5px] text-neutral-400 leading-relaxed">{f.body}</p>
                <div className="mt-5 flex items-start gap-2.5 border-l border-white/15 pl-4">
                  <span className="font-title text-[10px] uppercase tracking-[0.18em] text-neutral-500 pt-0.5 shrink-0">Business benefit</span>
                </div>
                <p className="mt-2 pl-4 text-[14px] text-neutral-300 leading-relaxed border-l border-white/15">{f.benefit}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Enquiry → notification workflow */}
        <Reveal delay={0.1} className="mt-16 sm:mt-24">
          <p className="font-title text-xs uppercase tracking-[0.2em] text-neutral-500 mb-8">How the enquiry system works</p>
          <Stagger className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-3" stagger={0.14}>
            {cs.transformation.workflow.map((w, i) => (
              <Item key={w.step} className="relative">
                <div className="h-full rounded-xl border border-white/[0.08] bg-white/[0.02] p-5">
                  <span className="font-title text-sm text-neutral-600 tabular-nums">0{i + 1}</span>
                  <h4 className="mt-3 font-title text-[15px] font-medium text-white tracking-tight">{w.step}</h4>
                  <p className="mt-2 text-[13px] text-neutral-500 leading-relaxed">{w.detail}</p>
                </div>
                {i < cs.transformation.workflow.length - 1 && (
                  <span className="hidden sm:grid place-items-center absolute top-1/2 -right-2 -translate-y-1/2 z-10 text-neutral-600">
                    <ArrowRight size={16} />
                  </span>
                )}
              </Item>
            ))}
          </Stagger>
        </Reveal>
      </SectionWrap>

      {/* SECTION 04 — THE COMMERCIAL OUTCOME */}
      <SectionWrap className="py-24 sm:py-32 lg:py-44">
        <Divider />
        <div className="mt-16 sm:mt-24 max-w-3xl">
          <Reveal><Eyebrow>{cs.outcome.number}</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 data-testid="cs-outcome-headline" className="mt-6 font-title font-medium text-white tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-[64px] text-balance">
              {cs.outcome.headline}
            </h2>
          </Reveal>
        </div>

        {/* Dominant stat */}
        <div className="mt-16 sm:mt-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <Reveal className="lg:col-span-7">
            <p className="font-title text-[10.5px] uppercase tracking-[0.22em] text-neutral-600 mb-4">{cs.outcome.stats[0].label}</p>
            <p data-testid="cs-stat-primary" className="font-title font-medium text-white tracking-tight leading-none text-7xl sm:text-8xl lg:text-[150px]">
              <Counter {...cs.outcome.stats[0]} />
            </p>
          </Reveal>
          <Stagger className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-8 lg:gap-7" stagger={0.14} delay={0.1}>
            {cs.outcome.stats.slice(1).map((s, i) => (
              <Item key={s.label} data-testid={`cs-stat-${i + 2}`} className="border-t border-white/[0.1] pt-4">
                <p className="font-title font-medium text-white tracking-tight leading-none text-4xl sm:text-5xl">
                  <Counter {...s} />
                </p>
                <p className="mt-3 text-[13px] text-neutral-500 leading-relaxed max-w-[26ch]">{s.label}</p>
              </Item>
            ))}
          </Stagger>
        </div>

        {/* Narrative + timeline */}
        <div className="mt-20 sm:mt-28 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <p className="text-[15px] sm:text-lg text-neutral-300 leading-relaxed">{cs.outcome.narrative}</p>
            <p className="mt-6 text-[12.5px] text-neutral-600 leading-relaxed max-w-xl italic">{cs.outcome.disclaimer}</p>
          </Reveal>
          <Stagger className="lg:col-span-5" stagger={0.16} delay={0.1} data-testid="cs-timeline">
            {cs.outcome.timeline.map((t, i) => (
              <Item key={t.date} className="relative pl-8 pb-8 last:pb-0">
                <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-white" />
                {i < cs.outcome.timeline.length - 1 && <span className="absolute left-[4.5px] top-4 bottom-0 w-px bg-white/15" />}
                <p className="font-title text-xs uppercase tracking-[0.18em] text-neutral-500">{t.date}</p>
                <p className="mt-2 text-[15px] text-white leading-snug">{t.label}</p>
              </Item>
            ))}
          </Stagger>
        </div>
      </SectionWrap>

      {/* SECTION 05 — BEFORE & AFTER */}
      <SectionWrap className="py-20 sm:py-28 lg:py-36">
        <Divider />
        <Reveal className="mt-16 sm:mt-24 max-w-3xl">
          <h2 data-testid="cs-comparison-headline" className="font-title font-medium text-white tracking-tight leading-[1.07] text-3xl sm:text-4xl lg:text-[52px] text-balance">
            {cs.comparison.headline}
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-12 sm:mt-14 max-w-3xl mx-auto">
          <BeforeAfterSlider before={cs.beforeImage} after={cs.afterImage} />
          <p className="mt-4 text-[12.5px] text-neutral-500 text-center">Drag the handle to compare the original website with the redesign.</p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          <Reveal>
            <div data-testid="cs-before" className="h-full rounded-2xl border border-white/[0.08] bg-white/[0.015] p-7 sm:p-9">
              <p className="font-title text-xs uppercase tracking-[0.2em] text-neutral-500 mb-7">Before</p>
              <ul className="space-y-4">
                {cs.comparison.before.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[14.5px] text-neutral-400">
                    <X size={16} className="mt-0.5 shrink-0 text-neutral-600" />{b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="cs-after" className="h-full rounded-2xl border border-white/[0.14] bg-white/[0.04] p-7 sm:p-9">
              <p className="font-title text-xs uppercase tracking-[0.2em] text-white mb-7">After</p>
              <ul className="space-y-4">
                {cs.comparison.after.map((a) => (
                  <li key={a} className="flex items-start gap-3 text-[14.5px] text-white">
                    <Check size={16} className="mt-0.5 shrink-0 text-white" />{a}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </SectionWrap>

      {/* SECTION 06 — FINAL SHOWCASE */}
      <SectionWrap className="py-20 sm:py-28 lg:py-36">
        <Divider />
        <div className="mt-16 sm:mt-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <h2 data-testid="cs-showcase-headline" className="font-title font-medium text-white tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-balance">
              {cs.showcase.headline}
            </h2>
            <p className="mt-6 text-[15px] text-neutral-400 leading-relaxed max-w-md">{cs.showcase.body}</p>
            <a
              data-testid="cs-live-link"
              href={cs.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-white text-black h-12 px-7 text-[14px] font-medium transition-transform duration-300 hover:-translate-y-0.5"
            >
              View Live Website
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.12}>
            <Parallax amount={50} className="rounded-2xl border border-white/10 bg-black shadow-[0_50px_140px_-50px_rgba(0,0,0,0.9)]">
              <img src={cs.heroMockup} alt={`${cs.client} website on a laptop`} className="w-full h-auto block" loading="lazy" />
            </Parallax>
          </Reveal>
        </div>
      </SectionWrap>

      {/* SECTION 07 — CTA */}
      <SectionWrap className="py-24 sm:py-32 lg:py-40">
        <div className="relative rounded-[24px] sm:rounded-[32px] border border-white/[0.08] bg-white/[0.02] overflow-hidden px-7 sm:px-14 py-16 sm:py-24 text-center">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[900px] rounded-full bg-white/[0.05] blur-[150px] pointer-events-none" />
          <Reveal><Eyebrow>{cs.cta.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 data-testid="cs-cta-headline" className="mt-7 font-title font-medium text-white tracking-tight leading-[1.07] text-3xl sm:text-5xl lg:text-[58px] text-balance max-w-4xl mx-auto">
              {cs.cta.headline}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 text-[15px] sm:text-base text-neutral-400 leading-relaxed max-w-2xl mx-auto">{cs.cta.body}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-11 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/audit" data-testid="cs-cta-audit">
                <Pill primary className="h-12 px-7 text-[14px]">Get Your Free Website Audit</Pill>
              </Link>
              <Link to="/#work" data-testid="cs-cta-work">
                <Pill className="h-12 px-7 text-[14px]">Explore More Projects</Pill>
              </Link>
            </div>
          </Reveal>
        </div>
      </SectionWrap>

      <HomeFooter />
    </main>
  );
};

export default CaseStudyPage;
